import { useEffect, useMemo, useState } from "react";
import { Drawer, Modal, Popconfirm, Select, Tooltip, message } from "antd";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { useFileUpload } from "../../hooks/useFileUpload";
import { careService } from "../../services/careService";

const pageItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const currentUserId = (user = {}) => user?.id || user?.userId || user?.uid || "";

const today = () => new Date().toISOString().slice(0, 10);

const toDateTimeInput = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return `${today()}T09:00`;
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

const toIsoDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const dateLabel = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatSeconds = (seconds) => {
  const total = Number(seconds || 0);
  if (!total) return "Not logged";
  const minutes = Math.floor(total / 60);
  const remaining = total % 60;
  if (!minutes) return `${remaining}s`;
  return `${minutes}m ${remaining}s`;
};

const formatFileSize = (size) => {
  const bytes = Number(size || 0);
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const nameWithEmail = (name, email, fallback = "") => {
  const displayName = String(name || "").trim();
  const displayEmail = String(email || "").trim();
  if (displayName && displayEmail) return `${displayName} (${displayEmail})`;
  return firstText(displayEmail, displayName, fallback);
};

const reportAttachmentName = (attachment = {}, index = 0) =>
  firstText(attachment.description, attachment.fileName, attachment.originalFileName, attachment.name, `Attachment ${index + 1}`);

const reportAttachmentType = (attachment = {}) => {
  const contentType = String(attachment.contentType || attachment.mimeType || attachment.type || "").toLowerCase();
  return contentType.startsWith("image/") ? "Photo" : "Document";
};

const isReportAttachmentPhoto = (attachment = {}) => {
  const contentType = String(attachment.contentType || attachment.mimeType || attachment.type || "").toLowerCase();
  const fileName = String(attachment.fileName || attachment.originalFileName || attachment.name || "").toLowerCase();
  return contentType.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|heic)$/.test(fileName);
};

const reportAttachmentUrl = (attachment = {}) =>
  firstText(attachment.localPreviewUrl, attachment.previewUrl, attachment.signedUrl, attachment.downloadUrl, attachment.url, attachment.fileUrl);

const reportAttachmentsFromPayload = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.attachments)) return payload.attachments;
  if (Array.isArray(payload.documents)) return payload.documents;
  if (Array.isArray(payload.files)) return payload.files;
  return [payload];
};

const staffName = (profile = {}) => {
  const user = profile.userProfile || profile.user || {};
  const name = firstText(
    [user.firstName, user.middleName, user.lastName].filter(Boolean).join(" "),
    [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(" "),
  );
  return nameWithEmail(name, firstText(user.email, profile.email), firstText(profile.id, "Staff member"));
};

const staffOptionId = (profile = {}) =>
  firstText(profile.id, profile.careProfileId, profile.userProfileId, profile.userId);

const workspaceMemberOptionId = (member = {}) =>
  firstText(member.user?.id, member.userId, member.id);

const workspaceMemberName = (member = {}) => {
  const memberUser = member.user || {};
  const name = firstText(
    [memberUser.firstName, memberUser.middleName, memberUser.lastName].filter(Boolean).join(" "),
  );
  return nameWithEmail(name, firstText(memberUser.email, member.email), firstText(member.id, "Workspace member"));
};

const residentName = (resident = {}) =>
  firstText(
    resident.preferredName,
    [resident.firstName, resident.middleName, resident.lastName].filter(Boolean).join(" "),
    resident.name,
    resident.id,
    "Resident",
  );

const statusClass = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "PLANNED") return "medium";
  if (normalized === "NEEDS_CORRECTION") return "medium";
  if (normalized === "MISSED" || normalized === "CANCELED") return "high";
  return "low";
};

const drillStatus = (drill = {}) => String(drill.status || "").trim().toUpperCase();

const hasSubmittedDrillReport = (drill = {}) => {
  const status = drillStatus(drill);
  return status === "COMPLETED" || status === "NEEDS_CORRECTION" || status === "MISSED";
};

const isLockedDrill = (drill = {}) => {
  const status = drillStatus(drill);
  return Boolean(status && status !== "PLANNED");
};

const observationFocusOptions = [
  ["ALARM_AUDIBILITY", "Alarm audibility"],
  ["STAFF_RESPONSE", "Staff response"],
  ["RESIDENT_ASSISTANCE", "Resident assistance"],
  ["EVACUATION_ROUTE", "Evacuation route"],
  ["MEETING_LOCATION", "Meeting location"],
  ["HEADCOUNT", "Headcount process"],
  ["EQUIPMENT_READINESS", "Equipment readiness"],
  ["COMMUNICATION", "Communication"],
  ["DOCUMENTATION", "Documentation"],
  ["OTHER", "Other"],
];

const notificationMethodOptions = [
  ["VERBAL_ANNOUNCEMENT", "Verbal announcement"],
  ["SMOKE_ALARM", "Smoke alarm"],
  ["FIRE_ALARM_SYSTEM", "Fire alarm system"],
  ["STAFF_INITIATED", "Staff initiated"],
  ["UNANNOUNCED", "Unannounced"],
];

const simulatedConditionOptions = [
  ["PRIMARY_EXIT_BLOCKED", "Primary exit blocked"],
  ["SECONDARY_EXIT_USED", "Secondary exit used"],
  ["RESIDENT_NEEDS_ASSISTANCE", "Resident needed assistance"],
  ["LOW_VISIBILITY", "Low visibility simulated"],
  ["NIGHTTIME_RESPONSE", "Nighttime response"],
  ["WEEKEND_STAFFING", "Weekend staffing"],
  ["MEDICATION_OR_RECORDS_GRAB", "Medication or records grab"],
  ["POWER_OUTAGE", "Power outage"],
  ["CALL_911_OR_EMERGENCY_CONTACT", "Emergency call process"],
  ["RELOCATION_SITE_CONTACTED", "Relocation site contacted"],
];

const weatherConditionOptions = [
  ["INDOOR_NORMAL", "Indoor / normal"],
  ["CLEAR", "Clear"],
  ["RAIN", "Rain"],
  ["SNOW_OR_ICE", "Snow or ice"],
  ["SMOKE_OR_POOR_AIR", "Smoke or poor air"],
  ["EXTREME_HEAT_OR_COLD", "Extreme heat or cold"],
];

const accountabilityStatusOptions = [
  ["ALL_ACCOUNTED_FOR", "All accounted for"],
  ["DELAYED_HEADCOUNT", "Delayed headcount"],
  ["RESIDENT_NOT_ACCOUNTED_IMMEDIATELY", "Resident not accounted immediately"],
  ["STAFF_NOT_ACCOUNTED_IMMEDIATELY", "Staff not accounted immediately"],
];

const evacuationOutcomeOptions = [
  ["COMPLETED_WITHIN_EXPECTATIONS", "Completed within expectations"],
  ["COMPLETED_WITH_COACHING", "Completed with coaching"],
  ["COMPLETED_WITH_DELAY", "Completed with delay"],
  ["STOPPED_FOR_SAFETY", "Stopped for safety"],
  ["NOT_COMPLETED", "Not completed"],
];

const equipmentIssueOptions = [
  ["NONE", "No equipment issues"],
  ["ALARM_NOT_AUDIBLE", "Alarm not audible"],
  ["EXIT_OBSTRUCTED", "Exit obstructed"],
  ["DOOR_OR_LOCK_ISSUE", "Door or lock issue"],
  ["LIGHTING_ISSUE", "Lighting issue"],
  ["EXTINGUISHER_ISSUE", "Extinguisher issue"],
  ["SUPPRESSION_SYSTEM_ISSUE", "Suppression system issue"],
  ["COMMUNICATION_ISSUE", "Communication issue"],
];

const shiftOptions = [
  ["DAY", "Day"],
  ["EVENING", "Evening"],
  ["NIGHT", "Night"],
  ["WEEKEND_DAY", "Weekend day"],
  ["WEEKEND_EVENING", "Weekend evening"],
  ["WEEKEND_NIGHT", "Weekend night"],
];

const shiftLabel = (value) => {
  const normalized = String(value || "").toUpperCase();
  if (normalized === "WEEKEND") return "Weekend";
  return shiftOptions.find(([optionValue]) => optionValue === normalized)?.[1] || firstText(value, "Day");
};

const optionLabel = (options, value, fallback = "Not selected") =>
  options.find(([optionValue]) => optionValue === String(value || "").toUpperCase())?.[1] || firstText(value, fallback);

const optionLabels = (options, values = []) => {
  const selected = Array.isArray(values) ? values : [];
  const labels = selected.map((value) => optionLabel(options, value, "")).filter(Boolean);
  return labels.length ? labels.join(", ") : "None selected";
};

const yesNo = (value) => (value ? "Yes" : "No");

const defaultEmergencyEvacuationDrillForm = (facilityName = "") => ({
  facilityName,
  drillDateTime: toDateTimeInput(),
  shift: "DAY",
  evacuationScope: "FULL_EVACUATION",
  drillDescription: "",
  observationFocus: "",
  observationFocusOther: "",
  observationChecklist: [],
  evacuationRoute: "",
  meetingLocation: "",
  evacuationTimeSeconds: "",
  residentsParticipated: "",
  residentParticipantIds: [],
  staffParticipated: "",
  allStaffParticipated: true,
  staffParticipantIds: [],
  residentsNeedingAssistance: "",
  smokeDetectorTested: true,
  sprinklerOrSuppressionTested: false,
  fireExtinguishersChecked: true,
  alarmAudible: true,
  notificationMethod: "VERBAL_ANNOUNCEMENT",
  simulatedConditions: [],
  weatherConditions: "INDOOR_NORMAL",
  accountabilityStatus: "ALL_ACCOUNTED_FOR",
  evacuationOutcome: "COMPLETED_WITHIN_EXPECTATIONS",
  equipmentIssues: ["NONE"],
  reportAttachments: [],
  cancelReason: "",
  canceledAt: "",
  canceledBy: "",
  completedBy: "",
  witnessedBy: "",
  status: "PLANNED",
  nextDrillDueDate: "",
  issuesFound: "",
  correctiveActions: "",
  notes: "",
});

const blankConductedReportFields = {
  evacuationTimeSeconds: "",
  residentsParticipated: "",
  residentParticipantIds: [],
  staffParticipated: "",
  allStaffParticipated: false,
  staffParticipantIds: [],
  residentsNeedingAssistance: "",
  smokeDetectorTested: false,
  sprinklerOrSuppressionTested: false,
  fireExtinguishersChecked: false,
  alarmAudible: false,
  notificationMethod: "",
  simulatedConditions: [],
  weatherConditions: "",
  accountabilityStatus: "",
  evacuationOutcome: "",
  equipmentIssues: [],
  reportAttachments: [],
  cancelReason: "",
  canceledAt: "",
  canceledBy: "",
  completedBy: "",
  witnessedBy: "",
  status: "",
  issuesFound: "",
  correctiveActions: "",
  notes: "",
};

const emergencyEvacuationDrillFormFromRecord = (drill = {}, facilityName = "") => ({
  ...defaultEmergencyEvacuationDrillForm(facilityName),
  facilityName,
  drillDateTime: toDateTimeInput(drill.drillDateTime),
  shift: drill.shift || "DAY",
  evacuationScope: drill.evacuationScope || drill.drillType || "FULL_EVACUATION",
  drillDescription: drill.drillDescription || "",
  observationFocus: drill.observationFocus || "",
  observationFocusOther: drill.observationFocusOther || "",
  observationChecklist: Array.isArray(drill.observationChecklist) ? drill.observationChecklist.map(String) : [],
  evacuationRoute: drill.evacuationRoute || "",
  meetingLocation: drill.meetingLocation || "",
  evacuationTimeSeconds: drill.evacuationTimeSeconds ?? "",
  residentsParticipated: drill.residentsParticipated ?? "",
  residentParticipantIds: Array.isArray(drill.residentParticipantIds) ? drill.residentParticipantIds.map(String) : [],
  staffParticipated: drill.staffParticipated ?? "",
  allStaffParticipated: drill.allStaffParticipated !== false,
  staffParticipantIds: Array.isArray(drill.staffParticipantIds) ? drill.staffParticipantIds.map(String) : [],
  residentsNeedingAssistance: drill.residentsNeedingAssistance ?? "",
  smokeDetectorTested: Boolean(drill.smokeDetectorTested),
  sprinklerOrSuppressionTested: Boolean(drill.sprinklerOrSuppressionTested),
  fireExtinguishersChecked: Boolean(drill.fireExtinguishersChecked),
  alarmAudible: Boolean(drill.alarmAudible),
  notificationMethod: drill.notificationMethod || "VERBAL_ANNOUNCEMENT",
  simulatedConditions: Array.isArray(drill.simulatedConditions) ? drill.simulatedConditions.map(String) : [],
  weatherConditions: drill.weatherConditions || "INDOOR_NORMAL",
  accountabilityStatus: drill.accountabilityStatus || "ALL_ACCOUNTED_FOR",
  evacuationOutcome: drill.evacuationOutcome || "COMPLETED_WITHIN_EXPECTATIONS",
  equipmentIssues: Array.isArray(drill.equipmentIssues) && drill.equipmentIssues.length ? drill.equipmentIssues.map(String) : ["NONE"],
  reportAttachments: reportAttachmentsFromPayload(drill.reportAttachments),
  cancelReason: drill.cancelReason || "",
  canceledAt: drill.canceledAt || "",
  canceledBy: drill.canceledBy || "",
  completedBy: drill.completedBy || "",
  witnessedBy: drill.witnessedBy || "",
  status: drill.status || "COMPLETED",
  nextDrillDueDate: drill.nextDrillDueDate ? toDateTimeInput(drill.nextDrillDueDate) : "",
  issuesFound: drill.issuesFound || "",
  correctiveActions: drill.correctiveActions || "",
  notes: drill.notes || "",
});

export default function EmergencyEvacuationDrillsPage() {
  const { user } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const { uploadFile, uploading: uploadingReportAttachment } = useFileUpload();
  const actorId = currentUserId(user);
  const automaticFacilityName = firstText(
    selectedWorkspace?.organization?.name,
    selectedWorkspace?.organizationName,
    selectedWorkspace?.organization?.legalName,
    selectedWorkspace?.name,
  );
  const [emergencyEvacuationDrills, setEmergencyEvacuationDrills] = useState([]);
  const [staffProfiles, setStaffProfiles] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingResidents, setLoadingResidents] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("PLAN");
  const [showPlanParticipants, setShowPlanParticipants] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelingDrill, setCancelingDrill] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [canceling, setCanceling] = useState(false);
  const [previewingReportAttachment, setPreviewingReportAttachment] = useState(null);
  const [form, setForm] = useState(() => defaultEmergencyEvacuationDrillForm(automaticFacilityName));
  const [expectedPlanForm, setExpectedPlanForm] = useState(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const workspaceParams = useMemo(
    () => ({ wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId }),
    [selectedWorkspaceId],
  );

  const loadEmergencyEvacuationDrills = () => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setEmergencyEvacuationDrills([]);
      return;
    }
    setLoading(true);
    setError("");
    careService.emergencyEvacuationDrills.list(workspaceParams)
      .then((data) => setEmergencyEvacuationDrills(pageItems(data)))
      .catch((err) => {
        setEmergencyEvacuationDrills([]);
        setError(err?.response?.data?.message || err?.message || "Unable to load emergency evacuation drills.");
      })
      .finally(() => setLoading(false));
  };

  const loadStaffProfiles = () => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setStaffProfiles([]);
      return;
    }
    setLoadingStaff(true);
    careService.careProfiles.list({ ...workspaceParams, status: "ACTIVE" })
      .then((data) => setStaffProfiles(pageItems(data)))
      .catch(() => setStaffProfiles([]))
      .finally(() => setLoadingStaff(false));
  };

  const loadResidents = () => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setResidents([]);
      return;
    }
    setLoadingResidents(true);
    careService.residents.list({ ...workspaceParams, status: "ACTIVE", size: 200 })
      .then((data) => setResidents(pageItems(data)))
      .catch(() => setResidents([]))
      .finally(() => setLoadingResidents(false));
  };

  useEffect(() => {
    loadEmergencyEvacuationDrills();
    loadStaffProfiles();
    loadResidents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkspaceId, workspaceListReady]);

  const workspaceMemberOptions = useMemo(
    () => (Array.isArray(selectedWorkspace?.users) ? selectedWorkspace.users : [])
      .map((member) => ({
        value: workspaceMemberOptionId(member),
        label: workspaceMemberName(member),
      }))
      .filter((option) => option.value),
    [selectedWorkspace?.users],
  );

  const staffOptions = useMemo(
    () => {
      const options = [
        ...workspaceMemberOptions,
        ...staffProfiles
      .map((profile) => ({
        value: staffOptionId(profile),
        label: staffName(profile),
      }))
      .filter((option) => option.value),
      ];
      const seen = new Set();
      return options.filter((option) => {
        if (seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      });
    },
    [staffProfiles, workspaceMemberOptions],
  );

  const allStaffIds = useMemo(() => staffOptions.map((option) => option.value), [staffOptions]);

  const staffLabelsById = useMemo(
    () => staffOptions.reduce((acc, option) => ({ ...acc, [option.value]: option.label }), {}),
    [staffOptions],
  );

  const residentOptions = useMemo(
    () => residents
      .map((resident) => ({
        value: firstText(resident.id, resident.residentId),
        label: residentName(resident),
      }))
      .filter((option) => option.value),
    [residents],
  );

  const residentLabelsById = useMemo(
    () => residentOptions.reduce((acc, option) => ({ ...acc, [option.value]: option.label }), {}),
    [residentOptions],
  );

  const staffParticipantLabel = (drill = {}) => {
    if (drill.allStaffParticipated) return "All staff";
    const ids = Array.isArray(drill.staffParticipantIds) ? drill.staffParticipantIds.map(String) : [];
    const names = ids.map((id) => staffLabelsById[id]).filter(Boolean);
    if (names.length) return names.join(", ");
    return ids.length ? `${ids.length} selected staff` : "Selected staff";
  };

  const plannedStaffNames = (plannedForm = {}) => {
    const ids = plannedForm.allStaffParticipated
      ? allStaffIds
      : Array.isArray(plannedForm.staffParticipantIds) ? plannedForm.staffParticipantIds.map(String) : [];
    const names = ids.map((id) => staffLabelsById[id]).filter(Boolean);
    if (names.length) return names.join(", ");
    if (plannedForm.allStaffParticipated) return "All staff and workspace members";
    return ids.length ? `${ids.length} selected staff` : "Not selected";
  };

  const plannedResidentNames = (plannedForm = {}) => {
    const ids = Array.isArray(plannedForm.residentParticipantIds) ? plannedForm.residentParticipantIds.map(String) : [];
    const names = ids.map((id) => residentLabelsById[id]).filter(Boolean);
    if (names.length) return names.join(", ");
    return ids.length ? `${ids.length} selected residents` : "Not selected";
  };

  const residentParticipantLabel = (drill = {}) => {
    const ids = Array.isArray(drill.residentParticipantIds) ? drill.residentParticipantIds.map(String) : [];
    const names = ids.map((id) => residentLabelsById[id]).filter(Boolean);
    if (names.length) return names.join(", ");
    return ids.length ? `${ids.length} selected residents` : "Residents not selected";
  };

  useEffect(() => {
    if (!drawerOpen || (!showPlanParticipants && drawerMode !== "REPORT") || !form.allStaffParticipated) return;
    setForm((current) => ({
      ...current,
      staffParticipantIds: allStaffIds,
      staffParticipated: allStaffIds.length ? String(allStaffIds.length) : current.staffParticipated,
    }));
  }, [allStaffIds, drawerMode, drawerOpen, form.allStaffParticipated, showPlanParticipants]);

  const metrics = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.toDateString());
    const completedDrills = emergencyEvacuationDrills.filter((drill) => {
      const status = String(drill.status || "").toUpperCase();
      return status === "COMPLETED" || status === "NEEDS_CORRECTION";
    });
    const latestCompleted = completedDrills
      .map((drill) => new Date(drill.updatedAt || drill.drillDateTime))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => b.getTime() - a.getTime())[0];
    const upcomingDue = emergencyEvacuationDrills
      .map((drill) => drill.nextDrillDueDate)
      .filter(Boolean)
      .map((date) => new Date(date))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => a.getTime() - b.getTime())[0];
    const overdueDrills = emergencyEvacuationDrills.filter((drill) => {
      const dueDate = drill.nextDrillDueDate ? new Date(drill.nextDrillDueDate) : null;
      return dueDate && !Number.isNaN(dueDate.getTime()) && dueDate < todayStart;
    }).length;
    return {
      completed: completedDrills.length,
      latestCompleted: latestCompleted ? dateLabel(latestCompleted) : "No completed drills",
      nextDue: upcomingDue
        ? upcomingDue.toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
        : "Not scheduled",
      overdue: overdueDrills,
    };
  }, [emergencyEvacuationDrills]);

  const filteredEmergencyEvacuationDrills = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return emergencyEvacuationDrills;
    return emergencyEvacuationDrills.filter((drill) => [
      drill.facilityName,
      automaticFacilityName,
      drill.shift,
      drill.evacuationScope,
      drill.drillDescription,
      drill.observationFocus,
      drill.observationFocusOther,
      Array.isArray(drill.observationChecklist) ? drill.observationChecklist.join(" ") : "",
      drill.notificationMethod,
      Array.isArray(drill.simulatedConditions) ? drill.simulatedConditions.join(" ") : "",
      drill.weatherConditions,
      drill.accountabilityStatus,
      drill.evacuationOutcome,
      Array.isArray(drill.equipmentIssues) ? drill.equipmentIssues.join(" ") : "",
      Array.isArray(reportAttachmentsFromPayload(drill.reportAttachments)) ? reportAttachmentsFromPayload(drill.reportAttachments).map((attachment, index) => reportAttachmentName(attachment, index)).join(" ") : "",
      drill.status,
      drill.completedBy,
      drill.witnessedBy,
      drill.evacuationRoute,
      drill.meetingLocation,
      drill.issuesFound,
      drill.correctiveActions,
      drill.cancelReason,
      drill.notes,
    ].join(" ").toLowerCase().includes(term));
  }, [automaticFacilityName, emergencyEvacuationDrills, search]);

  const isReportMode = drawerMode === "REPORT";
  const isDetailsMode = drawerMode === "DETAILS";
  const isReportLayoutMode = isReportMode || isDetailsMode;
  const expectedForm = isReportLayoutMode && expectedPlanForm ? expectedPlanForm : form;
  const reportSubmitted = hasSubmittedDrillReport(form);
  const hasReportColumn = isReportMode || reportSubmitted;
  const drawerWidth = isDetailsMode && !hasReportColumn ? "min(760px, 96vw)" : "min(1280px, 98vw)";
  const drawerTitle = isDetailsMode
    ? (
      <span className="emergency-evacuation-drawer-title">
        <span>Emergency Evacuation Drill Details</span>
        <em className={`pill ${statusClass(form.status)}`}>{firstText(form.status, "PLANNED")}</em>
      </span>
    )
    : isReportMode
    ? (editingId ? "Add Drill Report" : "Drill Report")
    : (editingId ? "Edit Drill Plan" : "Schedule Drill");

  const openNew = () => {
    setEditingId("");
    setDrawerMode("PLAN");
    setShowPlanParticipants(false);
    setExpectedPlanForm(null);
    setForm({
      ...defaultEmergencyEvacuationDrillForm(automaticFacilityName),
      allStaffParticipated: false,
      status: "PLANNED",
    });
    setDrawerOpen(true);
  };

  const openEdit = (drill) => {
    setEditingId(drill?.id || "");
    setDrawerMode("PLAN");
    setShowPlanParticipants(Boolean(drill?.allStaffParticipated) || Boolean(drill?.staffParticipantIds?.length));
    setExpectedPlanForm(null);
    setForm(emergencyEvacuationDrillFormFromRecord(drill, automaticFacilityName));
    setDrawerOpen(true);
  };

  const openDetails = (drill) => {
    const nextForm = emergencyEvacuationDrillFormFromRecord(drill, automaticFacilityName);
    setEditingId("");
    setDrawerMode("DETAILS");
    setShowPlanParticipants(false);
    setExpectedPlanForm(nextForm);
    setForm(nextForm);
    setDrawerOpen(true);
  };

  const openReport = (drill) => {
    setEditingId(drill?.id || "");
    setDrawerMode("REPORT");
    setShowPlanParticipants(true);
    setExpectedPlanForm(emergencyEvacuationDrillFormFromRecord(drill, automaticFacilityName));
    setForm({
      ...emergencyEvacuationDrillFormFromRecord(drill, automaticFacilityName),
      ...blankConductedReportFields,
    });
    setDrawerOpen(true);
  };

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateMultiSelectField = (field, value, checked, exclusiveValue = "") => {
    setForm((current) => {
      const currentValues = Array.isArray(current[field]) ? current[field] : [];
      let next = checked
        ? [...currentValues, value]
        : currentValues.filter((item) => item !== value);
      if (exclusiveValue && checked && value === exclusiveValue) {
        next = [exclusiveValue];
      } else if (exclusiveValue && checked) {
        next = next.filter((item) => item !== exclusiveValue);
      }
      return { ...current, [field]: Array.from(new Set(next)) };
    });
  };

  const updateAllStaffParticipated = (checked) => {
    setForm((current) => ({
      ...current,
      allStaffParticipated: checked,
      staffParticipantIds: checked ? allStaffIds : [],
      staffParticipated: checked && allStaffIds.length ? String(allStaffIds.length) : "",
    }));
  };

  const updateSelectedStaff = (staffIds) => {
    setForm((current) => ({
      ...current,
      staffParticipantIds: staffIds,
      staffParticipated: String(staffIds.length),
    }));
  };

  const showParticipants = () => {
    setShowPlanParticipants(true);
    if (!form.allStaffParticipated) return;
    setForm((current) => ({
      ...current,
      staffParticipantIds: allStaffIds,
      staffParticipated: allStaffIds.length ? String(allStaffIds.length) : current.staffParticipated,
    }));
  };

  const updateSelectedResidents = (residentIds) => {
    setForm((current) => ({
      ...current,
      residentParticipantIds: residentIds,
      residentsParticipated: String(residentIds.length),
    }));
  };

  const addReportAttachments = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const attachments = files.map((file) => ({
      id: `${Date.now()}-${file.name}-${Math.random().toString(16).slice(2)}`,
      file,
      fileName: file.name,
      originalFileName: file.name,
      contentType: file.type || "application/octet-stream",
      type: file.type?.startsWith("image/") ? "PHOTO" : "DOCUMENT",
      size: file.size,
      description: "",
      localPreviewUrl: URL.createObjectURL(file),
      uploaded: false,
    }));
    setForm((current) => ({
      ...current,
      reportAttachments: [...(current.reportAttachments || []), ...attachments],
    }));
    event.target.value = "";
  };

  const updateReportAttachment = (attachmentId, field, value) => {
    setForm((current) => ({
      ...current,
      reportAttachments: (current.reportAttachments || []).map((attachment) =>
        attachment.id === attachmentId ? { ...attachment, [field]: value } : attachment,
      ),
    }));
  };

  const removeReportAttachment = (attachmentId) => {
    setForm((current) => {
      const removed = (current.reportAttachments || []).find((attachment) => attachment.id === attachmentId);
      if (removed?.localPreviewUrl) URL.revokeObjectURL(removed.localPreviewUrl);
      return {
        ...current,
        reportAttachments: (current.reportAttachments || []).filter((attachment) => attachment.id !== attachmentId),
      };
    });
  };

  const previewReportAttachment = (attachment) => {
    const previewUrl = reportAttachmentUrl(attachment);
    if (!previewUrl) {
      message.error(attachment?.previewUnavailable ? "Preview is not available for this attachment." : "Preview URL is not available yet.");
      return;
    }
    setPreviewingReportAttachment(attachment);
  };

  const uploadReportAttachments = async () => {
    const uploadedAttachments = [];
    for (const attachment of form.reportAttachments || []) {
      if (attachment.fileId || !attachment.file) {
        const attachmentPayload = { ...attachment };
        delete attachmentPayload.file;
        delete attachmentPayload.localPreviewUrl;
        uploadedAttachments.push(attachmentPayload);
        continue;
      }
      const upload = await uploadFile(null, attachment.file, {
        confirmUpload: false,
        fileType: attachment.type || "DOCUMENT",
        includeUserId: false,
        preserveOriginal: true,
        purpose: "OTHER",
        skipUserValidation: true,
      });
      uploadedAttachments.push({
        id: attachment.id,
        fileId: upload?.fileId || upload?.file?.id || null,
        storageFileId: upload?.fileId || upload?.file?.id || null,
        fileName: attachment.fileName,
        originalFileName: attachment.originalFileName || attachment.fileName,
        description: attachment.description,
        contentType: attachment.contentType,
        type: attachment.type,
        size: attachment.size,
        storageStatus: "UPLOADED_NOT_CONFIRMED",
        uploaded: true,
        uploadedAt: new Date().toISOString(),
      });
    }
    return uploadedAttachments;
  };

  const numericOrNull = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    return Number(value);
  };

  const saveEmergencyEvacuationDrill = async (event) => {
    event.preventDefault();
    const drillDateTime = toIsoDateTime(form.drillDateTime);
    if (!drillDateTime) {
      message.error("Emergency evacuation drill date and time are required.");
      return;
    }
    const hasReportIssue = isReportMode && form.equipmentIssues.some((issue) => issue !== "NONE");
    if (isReportMode && !form.status) {
      message.error("Report status is required.");
      return;
    }
    if (isReportMode && (hasReportIssue || form.status === "NEEDS_CORRECTION") && !form.correctiveActions.trim()) {
      message.error("Corrective actions are required when a drill needs correction or an issue is reported.");
      return;
    }
    setSaving(true);
    try {
      const uploadedReportAttachments = isReportMode ? await uploadReportAttachments() : form.reportAttachments;
      const payload = {
        ...form,
        facilityName: automaticFacilityName || null,
        workspaceId: selectedWorkspaceId,
        drillDateTime,
        status: isReportMode ? form.status : "PLANNED",
        completedBy: isReportMode ? firstText(user?.email, [user?.firstName, user?.lastName].filter(Boolean).join(" "), actorId) : form.completedBy,
        evacuationScope: form.evacuationScope,
        observationChecklist: form.observationChecklist,
        simulatedConditions: form.simulatedConditions,
        equipmentIssues: form.equipmentIssues,
        reportAttachments: uploadedReportAttachments,
        evacuationTimeSeconds: numericOrNull(form.evacuationTimeSeconds),
        residentsParticipated: numericOrNull(form.residentsParticipated),
        residentParticipantIds: form.residentParticipantIds,
        staffParticipated: numericOrNull(form.staffParticipated),
        staffParticipantIds: form.staffParticipantIds,
        residentsNeedingAssistance: numericOrNull(form.residentsNeedingAssistance),
        nextDrillDueDate: form.nextDrillDueDate ? toIsoDateTime(form.nextDrillDueDate) : null,
      };
      const params = { actorId, ...workspaceParams };
      const saved = editingId
        ? await careService.emergencyEvacuationDrills.update(editingId, payload, params)
        : await careService.emergencyEvacuationDrills.create(payload, params);
      setEmergencyEvacuationDrills((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      setDrawerOpen(false);
      setEditingId("");
      setExpectedPlanForm(null);
      message.success("Emergency evacuation drill saved.");
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to save emergency evacuation drill.");
    } finally {
      setSaving(false);
    }
  };

  const deleteEmergencyEvacuationDrill = async (drill) => {
    if (!drill?.id) return;
    setDeletingId(drill.id);
    try {
      await careService.emergencyEvacuationDrills.delete(drill.id, { actorId, ...workspaceParams });
      setEmergencyEvacuationDrills((current) => current.filter((item) => item.id !== drill.id));
      message.success("Emergency evacuation drill deleted.");
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to delete emergency evacuation drill.");
    } finally {
      setDeletingId("");
    }
  };

  const openCancelDialog = (drill) => {
    setCancelingDrill(drill);
    setCancelReason("");
    setCancelDialogOpen(true);
  };

  const closeCancelDialog = () => {
    if (canceling) return;
    setCancelDialogOpen(false);
    setCancelingDrill(null);
    setCancelReason("");
  };

  const cancelEmergencyEvacuationDrill = async () => {
    const drill = cancelingDrill;
    const reason = cancelReason.trim();
    if (!drill?.id || canceling) return;
    if (!reason) {
      message.error("Cancel reason is required.");
      return;
    }
    setCanceling(true);
    try {
      const nextForm = emergencyEvacuationDrillFormFromRecord(drill, automaticFacilityName);
      const payload = {
        ...nextForm,
        facilityName: automaticFacilityName || null,
        workspaceId: selectedWorkspaceId,
        drillDateTime: toIsoDateTime(nextForm.drillDateTime),
        status: "CANCELED",
        cancelReason: reason,
        completedBy: null,
        observationChecklist: nextForm.observationChecklist,
        simulatedConditions: [],
        equipmentIssues: [],
        reportAttachments: [],
        evacuationTimeSeconds: numericOrNull(nextForm.evacuationTimeSeconds),
        residentsParticipated: numericOrNull(nextForm.residentsParticipated),
        residentParticipantIds: nextForm.residentParticipantIds,
        staffParticipated: numericOrNull(nextForm.staffParticipated),
        staffParticipantIds: nextForm.staffParticipantIds,
        residentsNeedingAssistance: numericOrNull(nextForm.residentsNeedingAssistance),
        nextDrillDueDate: nextForm.nextDrillDueDate ? toIsoDateTime(nextForm.nextDrillDueDate) : null,
      };
      const saved = await careService.emergencyEvacuationDrills.update(drill.id, payload, { actorId, ...workspaceParams });
      setEmergencyEvacuationDrills((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      setCancelDialogOpen(false);
      setCancelingDrill(null);
      setCancelReason("");
      message.success("Emergency evacuation drill canceled.");
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to cancel emergency evacuation drill.");
    } finally {
      setCanceling(false);
    }
  };

  const printReport = () => {
    document.body.classList.add("printing-emergency-evacuation-drill");
    window.setTimeout(() => {
      window.print();
      window.setTimeout(() => {
        document.body.classList.remove("printing-emergency-evacuation-drill");
      }, 250);
    }, 50);
  };

  const renderDrawerActions = (position = "bottom") => (
    <div className={`drawer-actions${position === "top" ? " drawer-actions-top" : ""}`}>
      {isReportLayoutMode ? (
        <button className="mini-btn emergency-evacuation-print-button" type="button" onClick={printReport}>
          <i className="ti ti-printer" /> Print Report
        </button>
      ) : null}
      <button className="mini-btn pharmacy-drawer-cancel" type="button" onClick={() => setDrawerOpen(false)}>{isDetailsMode ? "Close" : "Cancel"}</button>
      {!isDetailsMode ? (
        <button className="mini-btn pharmacy-primary-button" type="submit" disabled={saving || uploadingReportAttachment}>{saving || uploadingReportAttachment ? "Saving..." : isReportMode ? "Submit Report" : "Save Plan"}</button>
      ) : null}
    </div>
  );

  const previewingReportAttachmentUrl = reportAttachmentUrl(previewingReportAttachment || {});

  return (
    <CareShell className="dashboard-content emergency-evacuation-drills-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>Emergency Evacuation Drills</h1>
          <p>Plan evacuation drills, then document conducted drill performance, safety checks, and correction follow-up.</p>
        </div>
        <button className="mini-btn emergency-evacuation-schedule-button" type="button" onClick={openNew}>
          <i className="ti ti-calendar-plus" /> Schedule Drill
        </button>
      </div>

      <div className="medication-summary-grid emergency-evacuation-drill-summary-grid">
        <Panel title="Completed drills">
          <div className="resident-stat-value">{metrics.completed}</div>
        </Panel>
        <Panel title="Last drill">
          <div className="resident-stat-value compact">{metrics.latestCompleted}</div>
        </Panel>
        <Panel title="Next due">
          <div className={`resident-stat-value compact${metrics.overdue ? " text-danger" : ""}`}>{metrics.nextDue}</div>
        </Panel>
        <Panel title="Overdue drills">
          <div className={`resident-stat-value${metrics.overdue ? " text-danger" : ""}`}>{metrics.overdue}</div>
        </Panel>
      </div>

      <Panel title="Emergency Evacuation Drill Log">
        {error ? <p className="form-error">{error}</p> : null}
        <label className="resident-search medication-resident-search">
          <i className="ti ti-search" />
          <input placeholder="Search emergency evacuation drills" value={search} onChange={(event) => setSearch(event.target.value)} />
        </label>
        <div className="medication-table-wrap">
          <table className="medication-table emergency-evacuation-drill-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Scope</th>
                <th>Shift</th>
                <th>Evacuation</th>
                <th>Participants</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7}>Loading emergency evacuation drills...</td></tr> : null}
              {!loading && !filteredEmergencyEvacuationDrills.length ? <tr><td colSpan={7}>No emergency evacuation drills found.</td></tr> : null}
              {!loading && filteredEmergencyEvacuationDrills.map((drill) => (
                <tr key={drill.id || drill.drillDateTime}>
                  <td className="emergency-drill-date" data-label="Date">
                    <strong>{dateLabel(drill.drillDateTime)}</strong>
                    <small>Next due: {dateLabel(drill.nextDrillDueDate)}</small>
                  </td>
                  <td className="emergency-drill-scope" data-label="Scope">
                    {optionLabel([["FULL_EVACUATION", "Full evacuation"], ["PARTIAL_EVACUATION", "Partial evacuation"]], drill.evacuationScope, "Scope not added")}
                    <small>{firstText(drill.drillDescription, drill.observationFocus, drill.evacuationRoute, "Description not added")}</small>
                  </td>
                  <td data-label="Shift">{shiftLabel(drill.shift)}</td>
                  <td data-label="Evacuation">
                    {formatSeconds(drill.evacuationTimeSeconds)}
                    <small>{optionLabel(evacuationOutcomeOptions, drill.evacuationOutcome, "Outcome not added")}</small>
                  </td>
                  <td className="emergency-drill-participants" data-label="Participants">
                    {Number(drill.residentsParticipated || 0)} residents / {Number(drill.staffParticipated || 0)} staff
                    <small>{residentParticipantLabel(drill)} / {staffParticipantLabel(drill)}</small>
                  </td>
                  <td className="emergency-drill-status" data-label="Status"><em className={`pill ${statusClass(drill.status)}`}>{firstText(drill.status, "COMPLETED")}</em></td>
                  <td className="emergency-drill-actions" data-label="Actions">
                    <div className="medication-row-actions">
                      {isLockedDrill(drill) ? (
                        <Tooltip title="Details">
                          <button className="resident-icon-button" type="button" aria-label="View emergency evacuation drill details" onClick={() => openDetails(drill)}>
                            <i className="ti ti-eye" />
                          </button>
                        </Tooltip>
                      ) : (
                        <>
                          <Tooltip title="Details">
                            <button className="resident-icon-button" type="button" aria-label="View emergency evacuation drill details" onClick={() => openDetails(drill)}>
                              <i className="ti ti-eye" />
                            </button>
                          </Tooltip>
                          <Tooltip title="Add report">
                            <button className="resident-icon-button success" type="button" aria-label="Add drill report" onClick={() => openReport(drill)}>
                              <i className="ti ti-clipboard-check" />
                            </button>
                          </Tooltip>
                          <Tooltip title="Edit plan">
                            <button className="resident-icon-button" type="button" aria-label="Edit emergency evacuation drill" onClick={() => openEdit(drill)}>
                              <i className="ti ti-edit" />
                            </button>
                          </Tooltip>
                          <Tooltip title="Cancel scheduled drill">
                            <button className="resident-icon-button warning" type="button" aria-label="Cancel scheduled emergency evacuation drill" onClick={() => openCancelDialog(drill)}>
                              <i className="ti ti-ban" />
                            </button>
                          </Tooltip>
                          <Popconfirm
                            title="Delete emergency evacuation drill?"
                            description="This record will be removed from the log."
                            okText="Delete"
                            okButtonProps={{ danger: true, loading: deletingId === drill.id }}
                            onConfirm={() => deleteEmergencyEvacuationDrill(drill)}
                          >
                            <Tooltip title="Delete">
                              <button className="resident-icon-button danger" type="button" aria-label="Delete emergency evacuation drill">
                                <i className="ti ti-trash" />
                              </button>
                            </Tooltip>
                          </Popconfirm>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Drawer
        className="assessment-drawer medication-add-drawer emergency-evacuation-drill-drawer"
        destroyOnHidden
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        title={drawerTitle}
        width={drawerWidth}
      >
        <form className="assessment-drawer-form" onSubmit={saveEmergencyEvacuationDrill}>
          {renderDrawerActions("top")}
          {isReportLayoutMode ? (
            <div className={`emergency-evacuation-report-layout emergency-evacuation-report-print-area${!hasReportColumn ? " no-report" : ""}`}>
              <div className="emergency-evacuation-print-report">
                <header className="emergency-evacuation-print-report__header">
                  <div>
                    <p>Oliveeb Care</p>
                    <h1>Emergency Evacuation Drill Report</h1>
                    <span>{automaticFacilityName || "Current workspace organization"}</span>
                  </div>
                  <div>
                    <strong>{firstText(form.status, "PLANNED")}</strong>
                    <span>Printed {dateLabel(new Date().toISOString())}</span>
                  </div>
                </header>

                <section className="emergency-evacuation-print-report__section">
                  <h2>Drill Summary</h2>
                  <div className="emergency-evacuation-print-report__grid">
                    <span><strong>Facility</strong>{automaticFacilityName || "Current workspace organization"}</span>
                    <span><strong>Date and time</strong>{dateLabel(toIsoDateTime(expectedForm.drillDateTime))}</span>
                    <span><strong>Shift</strong>{shiftLabel(expectedForm.shift)}</span>
                    <span><strong>Evacuation scope</strong>{optionLabel([["FULL_EVACUATION", "Full evacuation"], ["PARTIAL_EVACUATION", "Partial evacuation"]], expectedForm.evacuationScope)}</span>
                    <span><strong>Route</strong>{firstText(expectedForm.evacuationRoute, "Not added")}</span>
                    <span><strong>Meeting location</strong>{firstText(expectedForm.meetingLocation, "Not added")}</span>
                    <span><strong>Next drill due</strong>{dateLabel(toIsoDateTime(expectedForm.nextDrillDueDate))}</span>
                  </div>
                </section>

                <section className="emergency-evacuation-print-report__section">
                  <h2>Expected Details</h2>
                  <div className="emergency-evacuation-print-report__text-block">
                    <strong>Drill description</strong>
                    <p>{firstText(expectedForm.drillDescription, "No planned description added.")}</p>
                  </div>
                  <div className="emergency-evacuation-print-report__text-block">
                    <strong>Observation focus</strong>
                    <p>{firstText(expectedForm.observationFocus, "No planned observation focus added.")}</p>
                  </div>
                  <div className="emergency-evacuation-print-report__text-block">
                    <strong>What to look out for</strong>
                    <p>{optionLabels(observationFocusOptions, expectedForm.observationChecklist)}</p>
                    {expectedForm.observationChecklist?.includes("OTHER") ? <p>{firstText(expectedForm.observationFocusOther, "Other focus not specified.")}</p> : null}
                  </div>
                </section>

                {hasReportColumn ? (
                  <section className="emergency-evacuation-print-report__section">
                    <h2>Conducted Drill Report</h2>
                    <div className="emergency-evacuation-print-report__grid">
                      <span><strong>Status</strong>{firstText(form.status, "Not submitted")}</span>
                      <span><strong>Evacuation time</strong>{formatSeconds(form.evacuationTimeSeconds)}</span>
                      <span><strong>Residents participated</strong>{Number(form.residentsParticipated || 0)}</span>
                      <span><strong>Staff participated</strong>{Number(form.staffParticipated || 0)}</span>
                      <span><strong>Residents needing assistance</strong>{Number(form.residentsNeedingAssistance || 0)}</span>
                      <span><strong>Witnessed by</strong>{firstText(form.witnessedBy, "Not added")}</span>
                      <span><strong>Notification method</strong>{optionLabel(notificationMethodOptions, form.notificationMethod, "Not selected")}</span>
                      <span><strong>Weather / conditions</strong>{optionLabel(weatherConditionOptions, form.weatherConditions, "Not selected")}</span>
                      <span><strong>Accountability</strong>{optionLabel(accountabilityStatusOptions, form.accountabilityStatus, "Not selected")}</span>
                      <span><strong>Outcome</strong>{optionLabel(evacuationOutcomeOptions, form.evacuationOutcome, "Not selected")}</span>
                      <span><strong>Residents involved</strong>{residentParticipantLabel(form)}</span>
                      <span><strong>Staff involved</strong>{staffParticipantLabel(form)}</span>
                    </div>
                    <div className="emergency-evacuation-print-report__checks">
                      <span>Smoke detectors tested: <strong>{yesNo(form.smokeDetectorTested)}</strong></span>
                      <span>Alarm audible throughout home: <strong>{yesNo(form.alarmAudible)}</strong></span>
                      <span>Fire extinguishers checked: <strong>{yesNo(form.fireExtinguishersChecked)}</strong></span>
                      <span>Sprinkler or suppression checked: <strong>{yesNo(form.sprinklerOrSuppressionTested)}</strong></span>
                    </div>
                  </section>
                ) : null}

                {hasReportColumn ? (
                  <section className="emergency-evacuation-print-report__section">
                    <h2>Findings and Follow-up</h2>
                    <div className="emergency-evacuation-print-report__text-block">
                      <strong>Simulated conditions</strong>
                      <p>{optionLabels(simulatedConditionOptions, form.simulatedConditions)}</p>
                    </div>
                    <div className="emergency-evacuation-print-report__text-block">
                      <strong>Equipment or process issues</strong>
                      <p>{optionLabels(equipmentIssueOptions, form.equipmentIssues)}</p>
                    </div>
                    <div className="emergency-evacuation-print-report__text-block">
                      <strong>Issues found</strong>
                      <p>{firstText(form.issuesFound, "No issues documented.")}</p>
                    </div>
                    <div className="emergency-evacuation-print-report__text-block">
                      <strong>Corrective actions</strong>
                      <p>{firstText(form.correctiveActions, "No corrective actions documented.")}</p>
                    </div>
                    <div className="emergency-evacuation-print-report__text-block">
                      <strong>Notes</strong>
                      <p>{firstText(form.notes, "No notes added.")}</p>
                    </div>
                  </section>
                ) : null}

                {form.reportAttachments?.length ? (
                  <section className="emergency-evacuation-print-report__section">
                    <h2>Photos and Documents</h2>
                    <div className="emergency-evacuation-print-report__attachments">
                      {form.reportAttachments.map((attachment, index) => (
                        <span key={attachment.id || attachment.fileId || `${attachment.fileName}-${index}`}>
                          <strong>{reportAttachmentName(attachment, index)}</strong>
                          {reportAttachmentType(attachment)} {formatFileSize(attachment.size) ? `| ${formatFileSize(attachment.size)}` : ""}
                        </span>
                      ))}
                    </div>
                  </section>
                ) : null}

                <section className="emergency-evacuation-print-report__section emergency-evacuation-print-report__participants">
                  <h2>Planned Participants</h2>
                  <div className="emergency-evacuation-print-report__text-block">
                    <strong>Planned residents</strong>
                    <p>{plannedResidentNames(expectedForm)}</p>
                  </div>
                  <div className="emergency-evacuation-print-report__text-block">
                    <strong>Planned staff</strong>
                    <p>{plannedStaffNames(expectedForm)}</p>
                  </div>
                </section>
              </div>
              <section className="assessment-drawer-section emergency-evacuation-plan-section emergency-evacuation-expected-section">
                <div className="emergency-evacuation-plan-header">
                  <h3>Expected Details</h3>
                  <span>Read only</span>
                </div>
                <div className="emergency-evacuation-expected-grid">
                  <span><strong>Facility</strong>{automaticFacilityName || "Current workspace organization"}</span>
                  <span><strong>Date and time</strong>{dateLabel(toIsoDateTime(expectedForm.drillDateTime))}</span>
                  <span><strong>Shift</strong>{shiftLabel(expectedForm.shift)}</span>
                  <span><strong>Evacuation scope</strong>{optionLabel([["FULL_EVACUATION", "Full evacuation"], ["PARTIAL_EVACUATION", "Partial evacuation"]], expectedForm.evacuationScope)}</span>
                  <span><strong>Next drill due</strong>{dateLabel(toIsoDateTime(expectedForm.nextDrillDueDate))}</span>
                  <span><strong>Route</strong>{firstText(expectedForm.evacuationRoute, "Not added")}</span>
                  <span><strong>Meeting location</strong>{firstText(expectedForm.meetingLocation, "Not added")}</span>
                  <span className="emergency-evacuation-expected-wide"><strong>Planned staff to participate</strong>{plannedStaffNames(expectedForm)}</span>
                </div>
                <div className="emergency-evacuation-expected-block">
                  <strong>Drill description</strong>
                  <p>{firstText(expectedForm.drillDescription, "No planned description added.")}</p>
                </div>
                <div className="emergency-evacuation-expected-block">
                  <strong>Observation focus</strong>
                  <p>{firstText(expectedForm.observationFocus, "No planned observation focus added.")}</p>
                </div>
                <div className="emergency-evacuation-expected-block">
                  <strong>What to look out for</strong>
                  <p>{optionLabels(observationFocusOptions, expectedForm.observationChecklist)}</p>
                  {expectedForm.observationChecklist?.includes("OTHER") ? <p>{firstText(expectedForm.observationFocusOther, "Other focus not specified.")}</p> : null}
                </div>
                {drillStatus(form) === "CANCELED" ? (
                  <div className="emergency-evacuation-expected-block">
                    <strong>Cancel reason</strong>
                    <p>{firstText(form.cancelReason, "No cancel reason captured.")}</p>
                  </div>
                ) : null}
              </section>

              {hasReportColumn ? (
              <fieldset className="emergency-evacuation-report-column" disabled={isDetailsMode}>
                <section className="assessment-drawer-section">
                <h3>Conducted Drill Report</h3>
                <div className="assessment-drawer-grid medication-add-grid">
                  <label className="ncp-form-field"><span>Evacuation time seconds</span><input type="number" min="0" value={form.evacuationTimeSeconds} onChange={(event) => updateForm("evacuationTimeSeconds", event.target.value)} /></label>
                  <label className="ncp-form-field"><span>Residents needing assistance</span><input type="number" min="0" value={form.residentsNeedingAssistance} onChange={(event) => updateForm("residentsNeedingAssistance", event.target.value)} /></label>
                  <label className="ncp-form-field"><span>Witnessed by</span><input value={form.witnessedBy} onChange={(event) => updateForm("witnessedBy", event.target.value)} /></label>
                  <label className="ncp-form-field"><span>Status</span><select value={form.status} onChange={(event) => updateForm("status", event.target.value)} required><option value="">Select status</option><option value="COMPLETED">Completed</option><option value="NEEDS_CORRECTION">Needs correction</option><option value="MISSED">Missed</option></select></label>
                  <label className="ncp-form-field"><span>Notification method</span><select value={form.notificationMethod} onChange={(event) => updateForm("notificationMethod", event.target.value)}><option value="">Select method</option>{notificationMethodOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
                  <label className="ncp-form-field"><span>Weather / conditions</span><select value={form.weatherConditions} onChange={(event) => updateForm("weatherConditions", event.target.value)}><option value="">Select conditions</option>{weatherConditionOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
                  <label className="ncp-form-field"><span>Accountability</span><select value={form.accountabilityStatus} onChange={(event) => updateForm("accountabilityStatus", event.target.value)}><option value="">Select accountability</option>{accountabilityStatusOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
                  <label className="ncp-form-field"><span>Outcome</span><select value={form.evacuationOutcome} onChange={(event) => updateForm("evacuationOutcome", event.target.value)}><option value="">Select outcome</option>{evacuationOutcomeOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
                </div>
                <div className="emergency-evacuation-drill-staff-selector">
                  <label className="ncp-form-field ncp-form-field-wide">
                    <span>Residents involved</span>
                    <Select
                      allowClear
                      loading={loadingResidents}
                      mode="multiple"
                      optionFilterProp="label"
                      options={residentOptions}
                      placeholder="Select residents involved"
                      value={form.residentParticipantIds}
                      onChange={updateSelectedResidents}
                    />
                  </label>
                  <label className="medication-inline-check">
                    <input type="checkbox" checked={form.allStaffParticipated} onChange={(event) => updateAllStaffParticipated(event.target.checked)} />
                    <span>All staff participated</span>
                  </label>
                  <label className="ncp-form-field ncp-form-field-wide">
                    <span>Staff involved</span>
                    <Select
                      allowClear
                      disabled={form.allStaffParticipated}
                      loading={loadingStaff}
                      mode="multiple"
                      optionFilterProp="label"
                      options={staffOptions}
                      placeholder={form.allStaffParticipated ? "All active staff selected" : "Select participating staff"}
                      value={form.staffParticipantIds}
                      onChange={updateSelectedStaff}
                    />
                  </label>
                </div>
                <div className="assessment-check-grid">
                  <label><input type="checkbox" checked={form.smokeDetectorTested} onChange={(event) => updateForm("smokeDetectorTested", event.target.checked)} /> Smoke detectors tested</label>
                  <label><input type="checkbox" checked={form.alarmAudible} onChange={(event) => updateForm("alarmAudible", event.target.checked)} /> Alarm audible throughout home</label>
                  <label><input type="checkbox" checked={form.fireExtinguishersChecked} onChange={(event) => updateForm("fireExtinguishersChecked", event.target.checked)} /> Fire extinguishers checked</label>
                  <label><input type="checkbox" checked={form.sprinklerOrSuppressionTested} onChange={(event) => updateForm("sprinklerOrSuppressionTested", event.target.checked)} /> Sprinkler or suppression system checked</label>
                </div>
              </section>

              <section className="assessment-drawer-section">
                <h3>Report Findings</h3>
                <fieldset className="ncp-form-field ncp-form-field-wide emergency-evacuation-observation-checklist">
                  <legend>Simulated conditions</legend>
                  <div>
                    {simulatedConditionOptions.map(([value, label]) => (
                      <label key={value}>
                        <input
                          type="checkbox"
                          checked={form.simulatedConditions.includes(value)}
                          onChange={(event) => updateMultiSelectField("simulatedConditions", value, event.target.checked)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <fieldset className="ncp-form-field ncp-form-field-wide emergency-evacuation-observation-checklist">
                  <legend>Equipment or process issues</legend>
                  <div>
                    {equipmentIssueOptions.map(([value, label]) => (
                      <label key={value}>
                        <input
                          type="checkbox"
                          checked={form.equipmentIssues.includes(value)}
                          onChange={(event) => updateMultiSelectField("equipmentIssues", value, event.target.checked, "NONE")}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <label className="ncp-form-field"><span>Issues found</span><textarea rows={3} value={form.issuesFound} onChange={(event) => updateForm("issuesFound", event.target.value)} placeholder="Optional detail for exceptions, failed steps, or delayed response." /></label>
                <label className="ncp-form-field"><span>Corrective actions</span><textarea rows={3} value={form.correctiveActions} onChange={(event) => updateForm("correctiveActions", event.target.value)} placeholder="Required when status is Needs correction or an issue is selected." /></label>
                <label className="ncp-form-field"><span>Notes</span><textarea rows={2} value={form.notes} onChange={(event) => updateForm("notes", event.target.value)} placeholder="Optional short note." /></label>
                <div className="emergency-evacuation-report-attachments">
                  <div className="emergency-evacuation-report-attachments__header">
                    <div>
                      <h4>Photos and Documents</h4>
                      <p>Optional evidence, exit issues, meeting point photos, or signed drill notes.</p>
                    </div>
                    {!isDetailsMode ? (
                    <label className="mini-btn emergency-evacuation-add-participants">
                      <i className="ti ti-paperclip" /> Add files
                      <input
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                        multiple
                        onChange={addReportAttachments}
                        type="file"
                      />
                    </label>
                    ) : null}
                  </div>
                  {form.reportAttachments?.length ? (
                    <div className="emergency-evacuation-report-attachment-list">
                      {form.reportAttachments.map((attachment, index) => {
                        const previewUrl = reportAttachmentUrl(attachment);
                        return (
                          <div className="emergency-evacuation-report-attachment" key={attachment.id || attachment.fileId || `${attachment.fileName}-${index}`}>
                            <div className="emergency-evacuation-report-attachment__preview">
                              {isReportAttachmentPhoto(attachment) && previewUrl ? (
                                <img src={previewUrl} alt="" />
                              ) : (
                                <i className="ti ti-file-description" />
                              )}
                            </div>
                            <div>
                              <strong>{reportAttachmentName(attachment, index)}</strong>
                              <small>{reportAttachmentType(attachment)} {formatFileSize(attachment.size) ? `| ${formatFileSize(attachment.size)}` : ""}</small>
                              <label className="ncp-form-field">
                                <span>Description</span>
                                <input
                                  value={attachment.description || ""}
                                  onChange={(event) => updateReportAttachment(attachment.id, "description", event.target.value)}
                                  placeholder="Describe what this attachment shows"
                                />
                              </label>
                            </div>
                            {!isDetailsMode ? (
                            <div className="emergency-evacuation-report-attachment__actions">
                              <Tooltip title="Preview">
                                <button className="resident-icon-button" type="button" aria-label="Preview attachment" onClick={() => previewReportAttachment(attachment)}>
                                  <i className="ti ti-eye" />
                                </button>
                              </Tooltip>
                              <button className="resident-icon-button danger" type="button" aria-label="Remove attachment" onClick={() => removeReportAttachment(attachment.id)}>
                                <i className="ti ti-trash" />
                              </button>
                            </div>
                            ) : (
                            <Tooltip title="Preview">
                              <button className="resident-icon-button" type="button" aria-label="Preview attachment" onClick={() => previewReportAttachment(attachment)}>
                                <i className="ti ti-eye" />
                              </button>
                            </Tooltip>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="emergency-evacuation-report-attachments__empty">No photos or documents added.</p>
                  )}
                </div>
              </section>
              </fieldset>
              ) : null}
            </div>
          ) : (
            <section className="assessment-drawer-section emergency-evacuation-plan-section">
              <div className="emergency-evacuation-plan-header">
                <h3>Drill Plan</h3>
                <span>Planned</span>
              </div>

              <div className="emergency-evacuation-plan-card">
                <div className="ncp-form-field emergency-evacuation-drill-facility-lock emergency-evacuation-plan-facility">
                  <span>Facility</span>
                  <strong>{automaticFacilityName || "Current workspace organization"}</strong>
                </div>

                <div className="emergency-evacuation-plan-grid">
                  <label className="ncp-form-field"><span>Date and time</span><input type="datetime-local" value={form.drillDateTime} onChange={(event) => updateForm("drillDateTime", event.target.value)} required /></label>
                  <label className="ncp-form-field"><span>Shift</span><select value={form.shift} onChange={(event) => updateForm("shift", event.target.value)}>{shiftOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
                  <label className="ncp-form-field"><span>Evacuation scope</span><select value={form.evacuationScope} onChange={(event) => updateForm("evacuationScope", event.target.value)} required><option value="FULL_EVACUATION">Full evacuation</option><option value="PARTIAL_EVACUATION">Partial evacuation</option></select></label>
                  <label className="ncp-form-field"><span>Next drill due</span><input type="datetime-local" value={form.nextDrillDueDate} onChange={(event) => updateForm("nextDrillDueDate", event.target.value)} /></label>
                </div>

                <label className="ncp-form-field ncp-form-field-wide"><span>Drill description</span><textarea rows={3} value={form.drillDescription} onChange={(event) => updateForm("drillDescription", event.target.value)} placeholder="Describe the planned drill scenario, route, staffing focus, or expected exercise." /></label>

                <label className="ncp-form-field ncp-form-field-wide"><span>Observation focus</span><textarea rows={3} value={form.observationFocus} onChange={(event) => updateForm("observationFocus", event.target.value)} placeholder="Describe what should be watched closely during the drill, such as resident assistance needs, blocked exits, staff response, communication, or headcount." /></label>

                <fieldset className="ncp-form-field ncp-form-field-wide emergency-evacuation-observation-checklist">
                  <legend>What to look out for</legend>
                  <div>
                    {observationFocusOptions.map(([value, label]) => (
                      <label key={value}>
                        <input
                          type="checkbox"
                          checked={form.observationChecklist.includes(value)}
                          onChange={(event) => {
                            const next = event.target.checked
                              ? [...form.observationChecklist, value]
                              : form.observationChecklist.filter((item) => item !== value);
                            updateForm("observationChecklist", next);
                          }}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                {form.observationChecklist.includes("OTHER") ? (
                  <label className="ncp-form-field ncp-form-field-wide"><span>Specify observation focus</span><input value={form.observationFocusOther} onChange={(event) => updateForm("observationFocusOther", event.target.value)} required /></label>
                ) : null}

                <div className="emergency-evacuation-plan-grid emergency-evacuation-plan-grid-secondary">
                  <label className="ncp-form-field"><span>Evacuation route</span><input value={form.evacuationRoute} onChange={(event) => updateForm("evacuationRoute", event.target.value)} /></label>
                  <label className="ncp-form-field"><span>Meeting location</span><input value={form.meetingLocation} onChange={(event) => updateForm("meetingLocation", event.target.value)} /></label>
                </div>

                <div className="emergency-evacuation-plan-participants">
                  {!showPlanParticipants ? (
                    <button className="mini-btn emergency-evacuation-add-participants" type="button" onClick={showParticipants}>
                      <i className="ti ti-users-plus" /> Add participants
                    </button>
                  ) : (
                    <>
                      <label className="medication-inline-check">
                        <input type="checkbox" checked={form.allStaffParticipated} onChange={(event) => updateAllStaffParticipated(event.target.checked)} />
                        <span>All staff and workspace members</span>
                      </label>
                      <label className="ncp-form-field ncp-form-field-wide">
                        <span>Participants</span>
                        <Select
                          allowClear
                          disabled={form.allStaffParticipated}
                          loading={loadingStaff}
                          mode="multiple"
                          optionFilterProp="label"
                          options={staffOptions}
                          placeholder={form.allStaffParticipated ? "All active staff and workspace members selected" : "Select staff or workspace members"}
                          value={form.staffParticipantIds}
                          onChange={updateSelectedStaff}
                        />
                      </label>
                    </>
                  )}
                </div>
              </div>
            </section>
          )}

          {renderDrawerActions("bottom")}
        </form>
      </Drawer>
      <Modal
        className="emergency-evacuation-attachment-preview-modal"
        destroyOnHidden
        footer={null}
        onCancel={() => setPreviewingReportAttachment(null)}
        open={Boolean(previewingReportAttachment)}
        title={previewingReportAttachment ? reportAttachmentName(previewingReportAttachment) : "Attachment Preview"}
        width="min(1120px, 96vw)"
      >
        {previewingReportAttachment && previewingReportAttachmentUrl ? (
          isReportAttachmentPhoto(previewingReportAttachment) ? (
            <img className="emergency-evacuation-attachment-preview-image" src={previewingReportAttachmentUrl} alt={reportAttachmentName(previewingReportAttachment)} />
          ) : (
            <iframe
              className="emergency-evacuation-attachment-preview-frame"
              src={previewingReportAttachmentUrl}
              title={reportAttachmentName(previewingReportAttachment)}
            />
          )
        ) : (
          <p className="emergency-evacuation-report-attachments__empty">Preview is not available for this attachment.</p>
        )}
      </Modal>
      <Modal
        className="emergency-evacuation-cancel-modal"
        destroyOnHidden
        okButtonProps={{ disabled: !cancelReason.trim(), loading: canceling }}
        okText="Cancel Drill"
        onCancel={closeCancelDialog}
        onOk={cancelEmergencyEvacuationDrill}
        open={cancelDialogOpen}
        title="Cancel Scheduled Drill"
      >
        <label className="ncp-form-field ncp-form-field-wide">
          <span>Cancel reason</span>
          <textarea
            autoFocus
            placeholder="Explain why this scheduled drill is being canceled."
            rows={4}
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
          />
        </label>
      </Modal>
    </CareShell>
  );
}
